/**
 * Use free gift module to display free gift associated with a SKU
 *
 * @module views/components/FreeGift
 * @memberof -Common
 */
import './FreeGift.scss';

import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import Text from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

import GiftItem from '../GiftItem/GiftItem';

/**
  * Represents a FreeGift component
  * @method
  * @param {FreeGiftProps} props - React properties passed from composition
  * @returns FreeGift
  */
export const FreeGift = function( props ){
  const freeGiftRef = useRef( null );

  const {
    backgroundColor,
    dataCapture,
    gifts,
    id,

    /* Rail Title */
    title,

    /* Rail meta data */
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    nextAccessibility,
    nextClickAction,
    previousAccessibility,
    previousClickAction
  } = props;

  /* Setting variables */
  const [currentIndex, updateCurrentIndex] = useState( 1 );
  const isSingleItem = gifts?.length === 1;
  const isLastItem =  currentIndex === gifts?.length;

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const _railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  const _railMeta = {
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  if( !hasItems( gifts ) ){
    return null;
  }

  return (
    <div className='FreeGift'
      id={ id }
      ref={ freeGiftRef }
    >
      <GridContainer>
        { title &&
          <div className='FreeGift__mobileTitle'>
            <Text
              htmlTag={ title.htmlTag }
              textStyle={ title.textStyle }
              color={ title.color }
              textAlign={ title.textAlign }
            >
              { title.text }
            </Text>
          </div>
        }
        <div className={
          classNames( 'FreeGift__container', {
            'FreeGift__container--singleItem': isSingleItem,
            'FreeGift__container--lastItem': isLastItem && !isSingleItem,
            'FreeGift__container--firstItem': !isSingleItem
          } )
        }
        >
          <Rail
            railRef={ freeGiftRef }
            id={ railUuid }
            items={ gifts }
            useFreeMode={ false }
            previousClickAction={ previousClickAction }
            nextClickAction={ nextClickAction }
            spaceBetween={ isSingleItem ? 0 : 16 }
            centeredSlides={ true }
            dataCapture={ dataCapture }
            _railADAmeta={ _railADAmeta }
            _railMeta={ _railMeta }
            slidesPerGroupXL={ 1 }
            sliderPerViewXL={ isSingleItem ? 1 : 1.2 }
            slidesPerGroupLG={ 1 }
            sliderPerViewLG={ isSingleItem ? 1 : 1.2 }
            slidesPerGroupMD={ 1 }
            sliderPerViewMD={ isSingleItem ? 1 : 1.5 }
            slidesPerGroupSM={ 1 }
            sliderPerViewSM={ isSingleItem ? 1 : 1.15 }
          >
            { gifts?.length > 0 && gifts.map( ( product, giftIndex ) => (
              <Slide key={ `FreeGift${ giftIndex + 1 } ` }>
                <div
                  className={
                    classNames( 'FreeGift__itemclass', {
                      ...( backgroundColor && { [`FreeGift__backgroundColor--${backgroundColor}`]: backgroundColor } ),
                      'FreeGift__itemclass--lastItem': !isSingleItem && isLastItem,
                      'FreeGift__itemclass--singleItem': isSingleItem,
                      'FreeGift__itemclass--firstItem': currentIndex === 1
                    } )
                  }
                >
                  <GiftItem
                    key={ giftIndex }
                    title={ title }
                    image={ product.image }
                    eyebrow={ product.eyebrow }
                    description={ product.description }
                    linkAction={ product.linkAction }
                  />
                </div>
              </Slide>
            ) ) }
          </Rail>
        </div>
      </GridContainer>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef FreeGiftProps
 * @type {object}
 * @property {string} backgroundColor - Sets the title for productRail list.
 * @property {string} carouselAccessibility - Sets the value for aria label for carousel
 * @property {string} carouselCardAccessibility - Sets carousel item number of total count string
 * @property {string} carouselDetailsAccessibility - Sets Carousel details screen reader information
 * @property {array} gifts - The array of Gift cards
 * @property {string} id - Sets the id value
 * @property {string} nextAccessibility - Sets the aria label for next button.
 * @property {object} nextClickAction - Action for when the next slide button is pressed
 * @property {string} previousAccessibility - Sets the aria label for previous button.
 * @property {object} previousClickAction - Action for when the previous slide button is pressed
 * @property {object} title - The object that contains the title.
 */
export const propTypes = {
  /**
   * The prop that holds backgroundColor for the carousel
   */
  backgroundColor: PropTypes.string,
  /**
   * Sets the value for aria label for carousel
   */
  carouselAccessibility: PropTypes.string,
  /**
   * Sets carousel item number of total count string
   */
  carouselCardAccessibility: PropTypes.string,
  /**
   * Sets Carousel details screen reader information
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
   * The array of Gift cards
   */
  gifts: PropTypes.array,
  /**
   * Sets the id value
   */
  id: PropTypes.string,
  /**
   * Sets the aria label for next button.
   */
  nextAccessibility: PropTypes.string,
  /**
   * Action for when the next slide button is pressed
   */
  nextClickAction: PropTypes.object,
  /**
   * Sets the aria label for previous button.
   */
  previousAccessibility: PropTypes.string,
  /**
   * Action for when the previous slide button is pressed
   */
  previousClickAction: PropTypes.object,
  /**
   * The object that contains the title.
   */
  title: PropTypes.object
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {bool} railAccessibility='carousel' - The default value for aria-label is set to text 'carousel'.
 */

FreeGift.propTypes = propTypes;
export default FreeGift;